import React from 'react';
import { Star, MapPin, MessageCircle, Globe, ExternalLink } from 'lucide-react';

/*
 * 2026-06-26 — Dedicated "Leave a Review" homepage section.
 * Voluntary, post-ride friendly. NO discounts/incentives. NO pricing logic.
 * Five external CTAs only. All open in new tab with safe rel attrs.
 *
 * Placement: rendered on Home.jsx right after the testimonials section
 * and before the Premier teaser, per growth-mode review-conversion plan.
 */

const REVIEW_LINKS = {
  googleReview: 'https://g.page/r/CRsSkN508n5DECA/review',
  googleMaps:   'https://goo.gl/maps/ZLgKpoDGJR3ahu1y6?g_st=ac',
  tripadvisor:  'https://www.tripadvisor.com/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
  facebook:     'https://www.facebook.com/share/1FwnCAwDLX/',
  website:      'https://www.taxi-98.com',
};

const buttons = [
  {
    key: 'google-review',
    label: 'Review us on Google',
    href: REVIEW_LINKS.googleReview,
    icon: Star,
    className: 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500',
  },
  {
    key: 'google-maps',
    label: 'View us on Google Maps',
    href: REVIEW_LINKS.googleMaps,
    icon: MapPin,
    className: 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500',
  },
  {
    key: 'tripadvisor',
    label: 'Review us on TripAdvisor',
    href: REVIEW_LINKS.tripadvisor,
    icon: Star,
    className: 'bg-[#00aa6c] hover:bg-[#008f5b] text-white border-[#00aa6c]',
  },
  {
    key: 'facebook',
    label: 'Follow or review us on Facebook',
    href: REVIEW_LINKS.facebook,
    icon: MessageCircle,
    className: 'bg-[#1877f2] hover:bg-[#0e63d4] text-white border-[#1877f2]',
  },
  {
    key: 'website',
    label: 'Visit Taxi-98.com',
    href: REVIEW_LINKS.website,
    icon: Globe,
    className: 'bg-cyan-700 hover:bg-cyan-800 text-white border-cyan-600',
  },
];

const LeaveReviewSection = () => {
  return (
    <section
      data-testid="home-leave-a-review-section"
      className="relative py-16 sm:py-20 bg-gradient-to-br from-cyan-50 via-white to-blue-50 border-y border-cyan-100"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <span
            data-testid="leave-a-review-eyebrow"
            className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-4"
          >
            Leave a Review
          </span>
          <h2
            data-testid="leave-a-review-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4"
          >
            Help future visitors book with confidence
          </h2>
          <p
            data-testid="leave-a-review-subtitle"
            className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto"
          >
            Your review helps future visitors book Taxi-98 with confidence. If Taxi-98 made your trip easier, safer, or more comfortable, please share your experience.
          </p>
        </div>

        <div
          data-testid="leave-a-review-buttons"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto"
        >
          {buttons.map(({ key, label, href, icon: Icon, className }) => (
            <a
              key={key}
              data-testid={`leave-a-review-btn-${key}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${label} (opens in new tab)`}
              className={`group inline-flex items-center justify-center gap-2 px-5 py-4 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base border-2 shadow-sm hover:shadow-md transition-all duration-300 ${className}`}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="leading-tight">{label}</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 flex-shrink-0" />
            </a>
          ))}
        </div>

        <p
          data-testid="leave-a-review-footnote"
          className="mt-8 text-center text-xs sm:text-sm text-gray-500 max-w-2xl mx-auto"
        >
          Honest reviews only — please share your real experience. Thank you for choosing Taxi-98 Bahamas.
        </p>
      </div>
    </section>
  );
};

export default LeaveReviewSection;
